import type { FormField, Submission, SubmissionStatus } from '@up2d8/contracts';
import { formatDate } from '../../shared/format';
import { labelOf } from './field-input';

/**
 * Hoe een aanvraag op het scherm staat: haar status zoals een mens ze leest, en of ze nog in te
 * trekken is. Eén plek voor de app, de bouwer en de inbox.
 *
 * Een formulier zonder behandeling ("Alleen verzamelen") wacht niet op een beslissing. Een
 * ingediende aanvraag is daar **Ontvangen**, niet "In behandeling", en heeft geen knop "Aanvraag
 * intrekken" (testrapport 28 okt., #8). De server weigert dat intrekken ook.
 */
export type DisplayStatus = SubmissionStatus | 'received';

export function displayStatus(
  submission: Pick<Submission, 'status' | 'needsHandling'>,
): DisplayStatus {
  if (submission.status === 'submitted' && submission.needsHandling === false) return 'received';
  return submission.status;
}

/** Alleen wat echt op een beslissing wacht, kan de indiener nog intrekken. */
export function canWithdraw(submission: Pick<Submission, 'status' | 'needsHandling'>): boolean {
  return displayStatus(submission) === 'submitted';
}

/**
 * Een datum uit een formulier (`YYYY-MM-DD`), opgemaakt zoals elders in de app: "10 nov. 2026".
 *
 * Als lokale middernacht gelezen en niet als UTC: `new Date('2026-11-10')` is in een zone ten
 * westen van Greenwich nog 9 november.
 */
function day(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return formatDate(new Date(`${value}T00:00:00`)) || value;
}

/**
 * Een ingevulde waarde in woorden.
 *
 * Datums stonden er rauw ("2026-11-10 → 2026-11-12" in Mijn aanvragen, testrapport 28 okt.).
 */
export function describeSubmissionValue(
  field: FormField,
  value: unknown,
  language: string,
): string {
  if (value === null || value === undefined || value === '') return '—';
  switch (field.type) {
    case 'date':
      return typeof value === 'string' ? day(value) : String(value);
    case 'daterange': {
      const range = value as { from?: string; to?: string };
      return range.from && range.to ? `${day(range.from)} → ${day(range.to)}` : '—';
    }
    case 'choice': {
      const option = field.options.find((item) => item.value === value);
      return option ? labelOf(option.label, language, option.value) : String(value);
    }
    case 'multichoice':
      return (value as string[])
        .map((item) => {
          const option = field.options.find((candidate) => candidate.value === item);
          return option ? labelOf(option.label, language, option.value) : item;
        })
        .join(', ');
    case 'photo':
      return 'foto';
    default:
      return String(value);
  }
}
